import { parseProgrammeZoneParams } from '@signage42/player-renderer';
import { apiRequest } from '@/api/client';
import { pushWarning } from '@/lib/warningsBuffer';
import type { ProgrammePayload } from '@signage42/player-renderer';

/**
 * The programme payload, fetched once and joined locally at render time.
 *
 * The payload is *data*, not content: it is fetched on its own cadence, cached,
 * and re-read by the zones on every tick (D6). That is what makes the day
 * rollover free — at 04:00 the same cached payload starts selecting a different
 * day, with no request, so a screen whose network died at midnight still turns
 * over correctly.
 *
 * The refetch interval is deliberately shorter than the one-hour presigned URL
 * lifetime in the payload's image links: a board that keeps rendering from an
 * hour-old payload would start showing broken posters. A refetch that fails
 * leaves the previous payload in place, because a stale programme is worth far
 * more on a foyer wall than an empty one.
 */

const REFETCH_INTERVAL_MS = 20 * 60 * 1000;
const RETRY_INTERVAL_MS = 60 * 1000;

/**
 * Days requested before any layout has been seen. Covers the default
 * `performance_schedule` zone (7 days) with room to spare, so the first render
 * after a cold start is already complete.
 */
const DEFAULT_DAYS = 10;

/** Matches the API's ceiling; asking for more is refused as a bad request. */
const MAX_DAYS = 31;

export class ProgrammeCache {
  private payload: ProgrammePayload | null = null;
  private days = DEFAULT_DAYS;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private inFlight: Promise<void> | null = null;
  private readonly listeners = new Set<(payload: ProgrammePayload | null) => void>();

  async start(): Promise<void> {
    await this.refetch();
  }

  stop(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  current(): ProgrammePayload | null {
    return this.payload;
  }

  subscribe(fn: (payload: ProgrammePayload | null) => void): () => void {
    this.listeners.add(fn);
    fn(this.payload);
    return () => {
      this.listeners.delete(fn);
    };
  }

  /**
   * Widens the requested window when a layout turns up asking for more days than
   * the cached payload covers — a "next month" board on a screen that also runs
   * a "tonight" board. Narrowing never happens: a zone that needs fewer days
   * simply ignores the extra, and shrinking would mean refetching every time the
   * playlist moved between the two.
   */
  ensureDays(days: number): void {
    const wanted = Math.min(Math.max(Math.trunc(days), 1), MAX_DAYS);
    if (wanted <= this.days) return;
    this.days = wanted;
    void this.refetch();
  }

  async refetch(): Promise<void> {
    // Collapse concurrent callers — a layout change and the interval can land
    // together, and the payload is the whole organization's window either way.
    if (this.inFlight) return this.inFlight;
    this.inFlight = this.fetchOnce();
    try {
      await this.inFlight;
    } finally {
      this.inFlight = null;
    }
  }

  private async fetchOnce(): Promise<void> {
    let nextIn = REFETCH_INTERVAL_MS;
    try {
      this.payload = await apiRequest<ProgrammePayload>('/player/performances', {
        query: { days: String(this.days) },
      });
      for (const listener of this.listeners) listener(this.payload);
    } catch (err) {
      // Keep rendering the previous payload. The service worker also serves the
      // last good response, so an offline player still has one.
      pushWarning(`programme-fetch-failed:${(err as Error).message ?? 'err'}`);
      nextIn = RETRY_INTERVAL_MS;
    } finally {
      this.schedule(nextIn);
    }
  }

  private schedule(delayMs: number): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      void this.refetch();
    }, delayMs);
  }
}

/**
 * The widest day range the programme zones of a layout ask for, or null when the
 * layout has none. Only a schedule zone spans days; a detail zone and a sponsor
 * strip render from today's rows.
 */
export function programmeDaysNeeded(
  zones: ReadonlyArray<{ parameters: Record<string, unknown> }>,
): number | null {
  let max: number | null = null;
  for (const zone of zones) {
    const params = parseProgrammeZoneParams(zone.parameters);
    if (params === null) continue;
    const days = params.kind === 'performance-schedule' ? params.days : 1;
    max = max === null ? days : Math.max(max, days);
  }
  return max;
}
